import { AcceptableFileType } from 'src/constants/acceptableFileTypes'

import { getExtension } from './fileUploadUtils'

export type MediaType =
  'MP4' | 'PDF' | 'JPEG' | 'PNG' | 'DICOM' | 'DOC_XLS_PPT' | 'ZIP'

type MediaTypeDetail = {
  mediaType: MediaType
  magicNumbers: Uint8Array[]
  magicNumberOffset?: number
  extensions: string[]
}

const encoder = new TextEncoder()

const MP4: MediaTypeDetail = {
  mediaType: 'MP4',
  magicNumbers: [
    encoder.encode('ftypavc1'),
    encoder.encode('ftypF4V'),
    encoder.encode('ftypF4P'),
    encoder.encode('ftypiso2'),
    encoder.encode('ftypisom'),
    encoder.encode('ftypmmp4'),
    encoder.encode('ftypmp41'),
    encoder.encode('ftypmp42'),
    encoder.encode('ftypNDSC'),
    encoder.encode('ftypNDSH'),
    encoder.encode('ftypNDSM'),
    encoder.encode('ftypNDSP'),
    encoder.encode('ftypNDSS'),
    encoder.encode('ftypNDXC'),
    encoder.encode('ftypNDXH'),
    encoder.encode('ftypNDXM'),
    encoder.encode('ftypNDXP'),
    encoder.encode('ftypNDXS'),
    encoder.encode('ftypqt'),
  ],
  magicNumberOffset: 4,
  extensions: ['mp4'],
}

const PDF: MediaTypeDetail = {
  mediaType: 'PDF',
  magicNumbers: [Uint8Array.from([0x25, 0x50, 0x44, 0x46, 0x2d])],
  extensions: ['pdf'],
}

const JPEG: MediaTypeDetail = {
  mediaType: 'JPEG',
  magicNumbers: [Uint8Array.from([0xff, 0xd8])],
  extensions: ['jpg', 'jpeg'],
}

const PNG: MediaTypeDetail = {
  mediaType: 'PNG',
  magicNumbers: [Uint8Array.from([0x89, 0x50, 0x4e, 0x47])],
  extensions: ['png'],
}

const DICOM: MediaTypeDetail = {
  mediaType: 'DICOM',
  magicNumbers: [Uint8Array.from([0x44, 0x49, 0x43, 0x4d])],
  magicNumberOffset: 128,
  extensions: ['dcm', ''], // DICOMは拡張子なしを許容
}

const DOC_XLS_PPT: MediaTypeDetail = {
  mediaType: 'DOC_XLS_PPT',
  magicNumbers: [
    Uint8Array.from([0x50, 0x4b, 0x03, 0x04]), // ZIP
    Uint8Array.from([0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]), // Compound File Binary Format
  ],
  extensions: ['doc', 'xls', 'ppt', 'docx', 'xlsx', 'pptx'],
}

const ZIP: MediaTypeDetail = {
  mediaType: 'ZIP',
  magicNumbers: [Uint8Array.from([0x50, 0x4b, 0x03, 0x04])],
  extensions: ['zip'],
}

const mediaTypeMap: Partial<Record<AcceptableFileType, MediaTypeDetail>> = {
  '.jpg': JPEG,
  '.jpeg': JPEG,
  '.png': PNG,
  '.pdf': PDF,
  '.mp4': MP4,
  '.dcm': DICOM,
  '.doc': DOC_XLS_PPT,
  '.docx': DOC_XLS_PPT,
  '.xls': DOC_XLS_PPT,
  '.xlsx': DOC_XLS_PPT,
  '.ppt': DOC_XLS_PPT,
  '.pptx': DOC_XLS_PPT,
  '.zip': ZIP,
}

const isEqualBytes = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

// ファイルをバイナリで読み込んで許可された種別のファイルか判定する
type Args = {
  file: File
  acceptType: AcceptableFileType[]
}
type Result =
  | {
      isValid: false
    }
  | {
      isValid: true
      mediaType: MediaType
    }
export const binaryValidateFileItem = async ({
  file,
  acceptType,
}: Args): Promise<Result> => {
  const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsArrayBuffer(file)
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error)
  })

  const bufferView = new Uint8Array(arrayBuffer)
  const mediaTypeDetails = new Set(
    acceptType
      .map(type => mediaTypeMap[type])
      .filter((item): item is MediaTypeDetail => item !== undefined),
  )

  for (const mediaTypeDetail of mediaTypeDetails) {
    const offset = mediaTypeDetail.magicNumberOffset ?? 0
    if (
      mediaTypeDetail.magicNumbers.some(magicNumber => {
        const buffer = bufferView.slice(offset, offset + magicNumber.length)
        return isEqualBytes(magicNumber, buffer)
      }) &&
      mediaTypeDetail.extensions.includes(getExtension(file.name))
    ) {
      return {
        isValid: true,
        mediaType: mediaTypeDetail.mediaType,
      }
    }
  }

  return {
    isValid: false,
  }
}
